import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import { buildGrowthPhaseNotificationPreferencesRow } from "@/lib/notifications/growth-phase-notification-defaults";
import { loadNearMeUserDefaultsPolicy } from "@/lib/notifications/near-me-user-defaults-policy";
import { profileLocationUsableForNearMe } from "@/lib/notifications/homepage-near-me-quick-opt-in";
import { normalizeProfilePhoneDigits } from "@/lib/notifications/near-me-whatsapp-preference-sync";

type Client = SupabaseClient<Database>;

export type ProfileLocationSlice = {
  location?: string | null;
  location_state_id?: string | null;
  location_lga_id?: string | null;
  location_neighborhood_id?: string | null;
};

/** Near-me defaults are only enabled when profile location can support matching. */
export function nearMeEnabledForProfileLocation(
  profile: ProfileLocationSlice,
): boolean {
  return profileLocationUsableForNearMe({
    location: profile.location ?? null,
    location_state_id: profile.location_state_id ?? null,
    location_lga_id: profile.location_lga_id ?? null,
    location_neighborhood_id: profile.location_neighborhood_id ?? null,
  });
}

/**
 * Insert default notification_preferences when missing (no overwrite of existing rows).
 * Runs on every signup/login path — not gated by growth-phase env flags.
 */
export async function ensureGrowthPhaseNotificationPreferences(
  client: Client,
  userId: string,
  options?: { nearMeEnabled?: boolean; phoneDigits?: string | null },
): Promise<{ created: boolean; error?: string }> {
  const { data: existing, error: readError } = await client
    .from("notification_preferences")
    .select("user_id")
    .eq("user_id", userId)
    .maybeSingle();

  if (readError) {
    console.error("notification_preferences ensure read:", readError);
    return { created: false, error: readError.message };
  }

  if (existing) {
    return { created: false };
  }

  const nearMePolicy = await loadNearMeUserDefaultsPolicy(client);

  let phoneDigits = normalizeProfilePhoneDigits(options?.phoneDigits ?? null);
  if (phoneDigits == null) {
    const { data: profile, error: profileError } = await client
      .from("profiles")
      .select("phone")
      .eq("id", userId)
      .maybeSingle();
    if (profileError) {
      console.error(
        "notification_preferences ensure profile phone:",
        profileError,
      );
    } else {
      phoneDigits = normalizeProfilePhoneDigits(profile?.phone ?? null);
    }
  }

  const row = buildGrowthPhaseNotificationPreferencesRow(userId, {
    nearMeEnabled: options?.nearMeEnabled ?? true,
    nearMePolicy,
    phoneDigits,
  });

  const { error: insertError } = await client
    .from("notification_preferences")
    .insert(row);

  if (insertError) {
    console.error("notification_preferences ensure insert:", insertError);
    return { created: false, error: insertError.message };
  }

  return { created: true };
}
